import { useOutletContext } from "react-router-dom";
import useFee from "feature-module/finance/hooks/useFee";
import useFeePayment from "feature-module/finance/hooks/useFeePayment";
import { useEffect, useState } from "react";
import AddEditFeeModal from "feature-module/finance/modals/AddEditFeeModal";
import AddFeePaymentModal from "feature-module/finance/modals/AddFeePaymentModal";
import EditFeeAmountModal from "feature-module/finance/modals/EditFeeAmountModal";
import FeesContent from "./FeesContent";
import useStudent from "../hooks/useStudent";
import Loader from "components/Common/loader";
import { showConfirmDialog } from "utils/confirmDialogUtils";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

type Ctx = { student: any | null };

const StudentFeesTab = () => {
  const { student } = useOutletContext<Ctx>();
  const [isModalAddPaymentOpen, setIsModalAddPaymentOpen] = useState(false);
  const [isEditFeeModalOpen, setIsEditFeeModalOpen] = useState(false);
  const [currentFee, setCurrentFee] = useState<any>(null);

  const {
    setSelectedFee,
    setIsModalOpen,
    selectedFee,
    handleCreate,
    handleUpdate,
    isModalOpen,
    fetchFeeTypes,
    feeTypes,
    handleDelete: handleDeleteFee
  } = useFee();

  const { fetchFeesByStudentId, fees, loading } = useStudent();
  const { t } = useTranslation();

  const {
    handleCreate: handleCreateFeePayment,
    paymentMethods,
    fetchPaymentMethods,
    handleDelete: handleDeleteFeePayment,
  } = useFeePayment();

  useEffect(() => {
    if (!student?.id) return;
    fetchFeesByStudentId({ studentId: student.id, include: "feeType,feePayments" });
    fetchFeeTypes();
    fetchPaymentMethods();
  }, [student]);

  const refreshStudent = async () => {
    if (!student?.id) return;
    fetchFeesByStudentId({ studentId: student.id, include: "feeType,feePayments" });
  };

  const handlePaymentClick = (fee: any) => {
    setSelectedFee(fee);
    setIsModalAddPaymentOpen(true);
  };

  const handleSaveFee = async (feeData: any) => {
    const result = await handleCreate(feeData);
    if (result) {
      await refreshStudent();
      setIsModalOpen(false);
    }
    return result;
  };

  const handleSaveFeePayment = async (feePaymentData: any) => {
    const res = await handleCreateFeePayment(feePaymentData);
    if (res) {
      await refreshStudent();
      setIsModalAddPaymentOpen(false);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    await handleDeleteFeePayment(paymentId);
    await refreshStudent();
  };

  const showDeleteFeeConfirm = (id: string) => {
    showConfirmDialog({
      title: t("are_you_sure_you_want_to_delete_this_fee"),
      icon: <ExclamationCircleOutlined />,
      content: t("this_action_cannot_be_undone"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        if (handleDeleteFee) {
          const res = await handleDeleteFee(id);
          if (res) {
            await refreshStudent();
          }
        }
      },
    });
  };

  const openEditFeeModal = (fee: any) => {
    setCurrentFee(fee);
    setSelectedFee(fee);
    setIsEditFeeModalOpen(true);
  };

  const handleSaveFeeAmount = async (amount: number) => {
    if (!currentFee) return false;
    const result = await handleUpdate(currentFee.id, { amount });
    if (result) {
      setIsEditFeeModalOpen(false);
      setCurrentFee(null);
      await refreshStudent();
      return true;
    }
    return false;
  };

  if (loading) return <Loader />;

  return (
    <>
      <FeesContent
        fees={fees}
        setSelectedFee={setSelectedFee}
        setIsModalOpen={setIsModalOpen}
        handlePaymentClick={handlePaymentClick}
        handleDeletePayment={handleDeletePayment}
        showDeleteFeeConfirm={showDeleteFeeConfirm}
        handleUpdateFeeAmount={openEditFeeModal}
      />

      <AddFeePaymentModal
        isOpen={isModalAddPaymentOpen}
        onClose={() => setIsModalAddPaymentOpen(false)}
        onSave={handleSaveFeePayment}
        feeId={selectedFee?.id || ""}
        amountNotPaid={selectedFee?.amountNotPaid || 0}
        paymentMethods={paymentMethods}
      />

      <AddEditFeeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveFee}
        students={[]}
        feeTypes={feeTypes}
        selectedStudent={student?.id}
        classes={[]}
      />

      <EditFeeAmountModal
        isOpen={isEditFeeModalOpen}
        onClose={() => {
          setIsEditFeeModalOpen(false);
          setCurrentFee(null);
        }}
        currentAmount={Number(currentFee?.amount || 0)}
        onSave={handleSaveFeeAmount}
      />
    </>
  );
};

export default StudentFeesTab;
